import { Classes, Spinner } from "@blueprintjs/core";
import { IconName } from "@blueprintjs/icons";
import classnames from "classnames";
import { MouseEvent, PureComponent, useContext } from "react";
import styled, { css } from "styled-components";

import autobind from "autobind-decorator";
import IntentContext, { Intent } from "core/components/IntentContext";
import { IViewProps, View } from "core/decorators/View";
import VerticalCenter from "../../fragments/VerticalCenter";
import { toBlockCss } from "../common/BlockStylesConfig";
import { toTypographyCss } from "../common/TypographyStylesConfig";
import IconView from "../icon/Icon.view";
import ButtonConfig from "./ButtonConfig";

export enum ButtonVariant {
	DEFAULT = "default",
	PRIMARY = "primary",
	SECONDARY = "secondary",
	MINIMAL = "minimal",
	CARD_ACTION = "card-action",
	MENU_BUTTON = "menu-button",
	TAG = "tag"
}

interface IStyledButtonProps {
	active?: boolean;
	config: ButtonConfig;
	disabled?: boolean;
}

const mapPropsToStyles = ({config, disabled}: IStyledButtonProps) => css`
	display: ${config.block ? "block" : "inline-block"};
	text-decoration: none;
	color: inherit;
	border: 0;
	-webkit-appearance: none;
    -moz-appearance: none;
	${toTypographyCss(config.typography)}
	${toBlockCss(config.defaultState)}
	&:hover {
		${toBlockCss(config.hoverState)}
	}
	&:active, &.active {
		${toBlockCss(config.activeState)}
	}
	${disabled && toBlockCss(config.disabledState)}
`;

const StyledAnchorButton = styled.a<IStyledButtonProps>(mapPropsToStyles);
const StyledButton = styled.button<IStyledButtonProps>(mapPropsToStyles);
const StyledSpan = styled.span<IStyledButtonProps>(mapPropsToStyles);

export interface IButtonViewProps extends IViewProps<ButtonConfig, ButtonVariant> {
	icon?: string | IconName | JSX.Element;
	isActive?: boolean;
	loading?: boolean;
	onClick?: (e: any) => void;
	onKeyDown?: (e: any) => void;
	href?: string;
	disabled?: boolean;
	button?: boolean;
	span?: boolean;
	attrs?: any;
	isFocusable?: boolean;
}

@View({ id: "button", configClass: ButtonConfig, variants: ButtonVariant })
export class ButtonView extends PureComponent<IButtonViewProps> {

	@autobind
	private getChildren() {
		const { config, loading, viewKey, children } = this.props;

		if(loading) {
			return (
				<Spinner size={Spinner.SIZE_SMALL} />
			);
		}

		if(config.iconClass) {
			return (
				<IconView icon={config.iconClass} viewKey={viewKey + ".icon"}>
					{children}
				</IconView>
			);
		}

		return children;
	}

	public render() {
		const { attrs, button, config, href, isActive, onClick, disabled, style, intent, span, isFocusable, onKeyDown } = this.props;

		const skeleton = Intent.LOADING === intent;
		const classes = classnames({active: isActive, [Classes.SKELETON]: skeleton});

		const props = {
			...attrs || {},
			children: (
				<VerticalCenter>
					{this.getChildren()}
				</VerticalCenter>
			),
			className: classes,
			active: isActive,
			config,
			disabled: skeleton || disabled,
			onClick,
			onKeyDown,
			style
		};

		if (span) {
			return (
				<StyledSpan {...props} />
			);
		}

		if (button) {
			return (
				<StyledButton {...props} type="submit" />
			);
		}

		return (
			<StyledAnchorButton {...props} href={href} target="_blank" style={style} tabIndex={isFocusable ? 0 : -1} />
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/button/Button.view.tsx